import type {CallCardMessage} from '@features/calls/call-card/call-card.component';
import {ConversationCall, ConversationCallOutcome} from './messenger.models';

/**
 * THE CALL LOG ON THE CLIENT (ITP-E-208-05, B-03). The server reads a conversation's calls from the stored call records:
 * a list item's `lastCall` and `conversationCalls(conversationId)`, each `{invitationId, type, outcome, startedAt,
 * durationSeconds}`. These two functions take such an answer to the port's `ConversationCall`, and a `ConversationCall` to
 * the fields the call rows already word themselves from, so a call shown from the log reads exactly as its call message
 * does. The call UI is unchanged (Q8): nothing here draws.
 */

/** How each outcome reads on a call row: the row's state and what the call was for the reader (`CallCardMessage`). */
const ROW_OF: Readonly<Record<ConversationCallOutcome, {callState: string, callOutcome?: string}>> = {
  InProgress: {callState: 'Live'},
  Answered: {callState: 'Ended', callOutcome: 'Joined'},
  Missed: {callState: 'Missed', callOutcome: 'Missed'},
  Declined: {callState: 'Missed', callOutcome: 'Missed'},
  Busy: {callState: 'Missed', callOutcome: 'Missed'},
  NoAnswer: {callState: 'Missed', callOutcome: 'Caller'},
  NotRung: {callState: 'Ended', callOutcome: 'NotRung'}
};

/**
 * One call of the server's answer as a `ConversationCall`, or null for one it cannot place: no id, or an outcome this
 * client does not know. A call that never connected carries no duration.
 */
export function conversationCallOf(answer: any): ConversationCall | null {
  if (!answer?.invitationId || !Object.prototype.hasOwnProperty.call(ROW_OF, answer.outcome)) {
    return null;
  }
  const call: ConversationCall = {
    invitationId: answer.invitationId,
    type: answer.type === 'Video' ? 'Video' : 'Voice',
    outcome: answer.outcome,
    startedAt: answer.startedAt
  };
  if (answer.durationSeconds > 0) {
    call.durationSeconds = answer.durationSeconds;
  }
  return call;
}

/**
 * A stored call as a call row reads it: the fields `CallCardComponent` words the row, its preview and its missed mark from.
 * "Voice call · 02:05" for an answered call, "Missed video call" for one the reader missed, declined or was busy for, "No
 * one answered" to the caller of an unanswered one, "A call started" while it lasts. Null without a call.
 */
export function callRowOf(call: ConversationCall | null | undefined): CallCardMessage | null {
  const row = call?.invitationId ? ROW_OF[call.outcome] : undefined;
  if (!row) {
    return null;
  }
  return {
    callInvitationId: call.invitationId,
    callType: call.type,
    callState: row.callState,
    callOutcome: row.callOutcome,
    callDurationSeconds: call.durationSeconds || 0,
    createdDate: call.startedAt
  };
}
